/**
 * The mosaic over a designed room's still while its first photoreal picture
 * renders (MosaicLayer): coarse blocks at first, finer as the picture nears,
 * so that just before it lands the still is at (or close to) the finishing
 * block size. How near the first picture is comes from the job's own
 * progress — the setup steps, then the first picture's percent — not from
 * a clock, so a slow queue holds the mosaic coarse and a fast render runs
 * it down quickly.
 */

import type { RenderJob } from './types';

/** How much of the setup (before the first picture) the first picture's wait is taken to be. */
const SETUP_SHARE = 0.3;
/** The job percent at which the setup is normally over and the first picture starts. */
const SETUP_PERCENT = 12;

/**
 * 0 … 1: how near the first photoreal picture is. 1 once it is in (or the
 * worker is past it); 0 while nothing has started.
 */
export function firstPictureProgress(job: RenderJob | null | undefined, firstDone: boolean): number {
  if (firstDone) return 1;
  if (!job) return 0;
  if (job.status === 'done') return 1;
  if (job.status !== 'claimed' && job.status !== 'rendering') return 0;
  if (!job.picture) return Math.min(1, Math.max(0, job.percent) / SETUP_PERCENT) * SETUP_SHARE;
  if (job.picture.index > 1) return 1;
  return SETUP_SHARE + (1 - SETUP_SHARE) * Math.min(1, Math.max(0, job.picture.percent) / 100);
}

/** The block size, px, at a progress: from `start` down to `end`, easing out so the last blocks go quickly. */
export function mosaicBlock(progress: number, start: number, end: number): number {
  const p = Math.min(1, Math.max(0, progress));
  const eased = 1 - Math.pow(1 - p, 1.6);
  return Math.max(1, Math.round(start + (end - start) * eased));
}
